import Filter from '../controls/Filter.js';
import AddFilterButton from '../controls/AddFilterButton.js';

function hasSimpleObjectConstructor(o) {
    return (o.constructor === undefined || o.constructor === Object);
}

function pluck(value, keys) {

    let i, len, newVal, key, k;
    const klen = keys.length;

    if (typeof value === 'object') {
        if (Array.isArray(value)) {

            const newArray = [];

            for (i = 0, len = value.length; i < len; i++) {
                const item = value[i];
                newVal = {};

                for (k = 0; k < klen; k++) {
                    key = keys[k];
                    if (item.hasOwnProperty(key)) {
                        newVal[key] = item[key];
                    }
                }

                newArray.push(newVal);
            }

            return newArray;

        } else if (hasSimpleObjectConstructor(value)) {
            newVal = {};

            for (k = 0; k < klen; k++) {
                key = keys[k];
                if (value.hasOwnProperty(key)) {
                    newVal[key] = value[key];
                }
            }

            return newVal;

        }
    }

    return value; // Probably a boolean or other primitive value
}

function deepClone(value) {
    if (value !== null && typeof value === 'object') {
        if (Array.isArray(value)) {
            let a = value.slice(0);
            for (let i = 0, c = a.length; i < c; i++) {
                const e = a[i];
                if (e !== null && typeof e === 'object') {
                    a[i] = deepClone(e);
                }
            }
            return a;
        } else if (hasSimpleObjectConstructor(value)) {
            let o = Object.assign({}, value);
            for (let key of Object.keys(o)) {
                let e = o[key];
                if (e !== null && typeof e === 'object') {
                    o[key] = deepClone(e);
                }
            }
            return o;
        }
    }
    return value;
}

const isValuelessMode = function (mode) {
    if (!mode || typeof mode !== 'string') return false;

    const comparison = mode.substr(-1);

    //noinspection PointlessBooleanExpressionJS
    return !!(comparison === 'H' ||
        comparison === 'h' ||
        comparison === 'd' ||
        comparison === 'w' ||
        comparison === 'm' ||
        comparison === 'y');
};

class FiltersController {
    /**
     * @param {Object} options
     * @param {Object<String, Filter.Config>} options.config
     * @param {String?} options.saveKey
     * @param {Object?} options.defaults
     * @param {Object?} options.startWithValues
     * @param {jQuery?} options.$panel
     * @param {Function?} options.callback
     * @param {{i18n: {formatDate: function, t: function}, getUserSetting: function(key), setUserSetting: function(key, value)}} options.util
     */
    constructor(options) {
        options = options || {};

        this.util = options.util;

        this.$panel = options.$panel;
        this._currentFilters = [];

        // Set the defaults
        this._defaults = options.defaults;

        // Set the save key
        this._saveKey = options.saveKey;

        // Set the filters configuration
        this._config = options.config;

        // Build the "add filter" button
        const addFilterButton = this._addFilterButton = new AddFilterButton({
            config: this._config,
            i18n: this.util.i18n,
            text: this.util.i18n.t('filters', 'add_filter'),
        });

        if (this.$panel) {
            this.$panel.append(addFilterButton.render().$el);
        }

        addFilterButton.on('add', (filter) => {
            this._currentFilters.push(filter);
            this._setHooks(filter);
        });

        this._filtersCallback = options.callback;

        if (!options.startWithValues || Object.keys(options.startWithValues).length === 0) {
            // We do not have any "startup" values
            // Load saved filters
            const savedFilters = this._saveKey ? this.getUserSavedFilters(this._saveKey) : null;
            if (savedFilters && Object.keys(savedFilters).length) {
                // We have saved filters, load them
                this.setValues(savedFilters);
            } else {
                // We do not have saved filters, load the defaults
                this.setValues(this._defaults);
            }
        } else {
            // We have startup values, load them
            this.setValues(options.startWithValues);
        }
    }

    getUserSavedFilters(key) {
        return this.util.getUserSetting('filters_' + key);
    }

    setUserSavedFilters(key, conf) {
        return this.util.setUserSetting('filters_' + key, conf);
    }

    /**
     * @private
     * @param type
     * @param action
     * @param value
     * @param value2
     * @param filterMode
     * @returns {FiltersController}
     */
    _saveFiltersAndCallback(type, action, value, value2, filterMode) {
        const values = this.getValues(false);
        const jsonifiedValues = JSON.stringify(values);
        if (jsonifiedValues === JSON.stringify(this._defaults || {}) || jsonifiedValues === '{}') {
            this.setUserSavedFilters(this._saveKey, {});
        } else {
            this.setUserSavedFilters(this._saveKey, values);
        }

        if (this._filtersCallback) {
            this._filtersCallback(values, type, action, value, value2, filterMode);
        }

        return this;
    }

    /**
     * @private
     * @param filter
     * @returns {FiltersController}
     */
    _setHooks(filter) {
        if (this.$panel) {
            // Trigger a 'resize' event in jQuery,
            // Allowing the panel to re-layout other stuff based on new filter controls added to it
            this.$panel.resize();
        }

        filter
            .on('remove', () => {
                this._currentFilters.remove(filter);
                if (filter.type() !== undefined) { // If it's not a new/empty filter
                    this._saveFiltersAndCallback(filter.type(), 'remove');
                }
            })
            .on('value', (value, andValue) => {
                this._saveFiltersAndCallback(filter.type(), 'change', value, andValue, filter.mode());
            })
            .on('typeselect', type => {

                if (!this._config[type]) return;

                const filterDef = this._config[type];

                if (filterDef.type !== Filter.FilterType.BOOLEAN &&
                    filterDef.allowMultiple) return;

                const existing = this._currentFilters.filter(x => x !== filter && x.type() === type)[0];
                if (existing) {
                    filter.removeFilter();
                    existing.focusValueInput();
                }
            })
            .on('sizechange', () => {
                this.$panel.resize();
            })
            .on('afterremove', () => {
                this.$panel.resize();
            });

        return this;
    }

    /**
     * @public
     * @expose
     * @param filterValues
     * @returns {FiltersController}
     */
    setValues(filterValues) {
        // Clear old filters

        for (let i = 0; i < this._currentFilters.length; i++) {
            this._currentFilters[i].remove();
        }
        this._currentFilters.length = 0;

        // Load the new filters

        filterValues = filterValues || {};
        const configs = this._config;

        for (let type in filterValues) {

            if (!filterValues.hasOwnProperty(type)) continue;
            if (!configs.hasOwnProperty(type)) continue;

            let filterValue = filterValues[type];
            const config = configs[type];

            const supportsMultiValues = config.type === Filter.FilterType.MULTIOPTION ||
                (config.type === Filter.FilterType.BOOLEAN && config.allowMultiBoolean);

            // Normalize format
            if (supportsMultiValues) {
                let value = filterValue['value'];
                if (value === undefined && Array.isArray(filterValue)) {
                    value = filterValue;
                    filterValues[type] = filterValue = {'value': filterValue};
                }

                if (!Array.isArray(value)) {
                    value = filterValue['value'] = [value];
                }

                for (let i = 0; i < value.length; i++) {
                    if (value[i]['value'] === undefined) {
                        value[i] = {'value': value[i]};
                    }
                }

                for (let v of value) {
                    if (v['value'] === -1) {
                        v['value'] = Filter.SelectAllValue;
                    }
                }
            }

            const prepareFilterView = options => {
                const filter = new Filter(options);

                if (this.$panel) {
                    this.$panel.find('>.filter-add,>.filter').last().after(filter.render().$el);
                }

                this._currentFilters.push(filter);
                this._setHooks(filter);
            };

            if (supportsMultiValues) {
                // Localize if possible
                if (config.source && !(config.source instanceof Array) && (typeof config.source !== 'function')) {
                    let localizedItems = [];
                    let values = filterValue['value'];
                    let localized;

                    for (let i = 0; i < values.length; i++) {

                        let value = values[i]['value'];

                        if (config.type === Filter.FilterType.MULTIOPTION) {
                            localized = config.source[values[i]['value']];
                        } else if (config.type === Filter.FilterType.BOOLEAN) {
                            localized = value ? this.util.i18n.t('yes') : this.util.i18n.t('no');
                        }

                        localizedItems.push({value: value, label: localized});
                    }
                    filterValue['value'] = localizedItems;
                }

                // Create the filter view
                prepareFilterView({
                    config: configs,
                    i18n: this.util.i18n,
                    type: type,
                    mode: filterValue['mode'],
                    value: filterValue['value'],
                    value2: filterValue['value2'],
                    readonly: config.selectionEnabled === false,
                });
            } else {
                let values = filterValue instanceof Array ? filterValue :
                    ((filterValue['value'] instanceof Array) ? filterValue['value'] : [filterValue]);
                for (const filterValue of values) {

                    // Localize if possible
                    if (config.source && !(config.source instanceof Array) && (typeof config.source !== 'function')) {
                        if (config.type === Filter.FilterType.OPTION) {
                            filterValue['value'] = {
                                value: filterValue['value'],
                                label: config.source[filterValue['value']],
                            };
                        }
                    } else if (config.type === Filter.FilterType.DATETIME ||
                        config.type === Filter.FilterType.DATE) {

                        filterValue['value'] = new Date(filterValue['value']);
                        if (isNaN(+filterValue['value'])) {
                            filterValue['value'] = null;
                        }

                        if (filterValue['value2']) {
                            filterValue['value2'] = new Date(filterValue['value2']);
                            if (isNaN(+filterValue['value2'])) {
                                filterValue['value2'] = null;
                            }
                        }
                    }

                    // Create the filter view
                    prepareFilterView({
                        config: configs,
                        i18n: this.util.i18n,
                        type: type,
                        mode: filterValue['mode'],
                        value: filterValue['value'],
                        value2: filterValue['value2'],
                        readonly: config.selectionEnabled === false,
                    });

                }
            }
        }

        return this;
    }

    /**
     * @public
     * @expose
     * @param saveKey
     * @returns {FiltersController}
     */
    setSaveKey(saveKey) {
        this._saveKey = saveKey;
        return this;
    }

    /**
     * @public
     * @expose
     * @param key
     * @returns {FiltersController}
     */
    clearAutoCompleteCache(key) {
        if (!this._currentFilters) return this;

        const filters = this._currentFilters;

        for (let i = 0; i < filters.length; i++) {
            if (filters[i].type() === key) {
                filters[i].uncacheSource();
            }
        }

        return this;
    }

    /**
     * @public
     * @expose
     * @param pick
     * @param omitLabels
     * @returns {{}}
     */
    getValues(pick, omitLabels) {
        const values = {};
        if (arguments[1] === undefined && typeof (arguments[0]) === 'boolean') {
            omitLabels = arguments[0];
            pick = undefined;
        }

        pick = (pick && !Array.isArray(pick)) ? [pick] : pick;

        if (this._currentFilters) {

            for (let filter of this._currentFilters) {

                let filterValue = deepClone(filter.value());
                const config = filter._config,
                    type = filter.type();

                const supportsMultiValues = config.type === Filter.FilterType.MULTIOPTION ||
                    (config.type === Filter.FilterType.BOOLEAN && config.allowMultiBoolean);

                if (filterValue == null && !isValuelessMode(filter.mode())) continue;
                if (config.type === Filter.FilterType.MULTIOPTION && filterValue.length === 0) continue;
                if (type == null) continue;
                if (pick && pick.indexOf(type) === -1) continue;

                if (supportsMultiValues) {
                    let selectAllItem = Array.isArray(filterValue) && filterValue.find(x => x.value === Filter.SelectAllValue);
                    if (selectAllItem) {
                        selectAllItem.value = -1;
                        filterValue = [selectAllItem];
                    }
                }

                const value = {};

                if (filter.mode() != null) {
                    value['mode'] = filter.mode();
                }
                if (filterValue != null) {
                    value['value'] = filterValue;

                    if (omitLabels) {
                        value['value'] = pluck(value['value'], ['value']);
                    } else {
                        value['value'] = pluck(value['value'], ['value', 'label']);
                    }

                    if (value['value'] && config.type === Filter.FilterType.DATE) {
                        value['value'] = this.util.i18n.formatDate(new Date(value['value']), 'yyyy-MM-dd');
                    }
                }
                if (filter.value2() != null) {
                    value['value2'] = filter.value2();

                    if (omitLabels) {
                        value['value2'] = pluck(value['value2'], ['value']);
                    } else {
                        value['value2'] = pluck(value['value2'], ['value', 'label']);
                    }

                    if (value['value2'] && config.type === Filter.FilterType.DATE) {
                        value['value2'] = this.util.i18n.formatDate(new Date(value['value2']), 'yyyy-MM-dd');
                    }
                }
                if (value['mode'] === '<>' && value['value2'] == null) {
                    continue;
                }
                if (config.source && !(config.source instanceof Array) && (typeof config.source !== 'function')) {
                    if (config.type === Filter.FilterType.MULTIOPTION) {
                        value['value'] = pluck(value['value'], ['value']);
                    } else if (config.type === Filter.FilterType.OPTION) {
                        value['value'] = value['value'].value;
                    }
                }

                if (values[type]) {
                    if (supportsMultiValues) {
                        values[type]['value'] = values[type]['value'].concat(value['value']);
                    } else {
                        if (values[type]['value'] instanceof Array) {
                            values[type]['value'] = values[type]['value'].concat(value);
                        } else {
                            values[type] = {'value': [values[type], value]};
                        }
                    }
                } else {
                    values[type] = value;

                }
            }
        }
        return values;
    }

    /**
     * Removes a filter by name, and calls the remove callback
     * @public
     * @expose
     * @param {String} filterName
     * @returns {FiltersController}
     */
    removeFilter(filterName) {
        const existing = this._currentFilters.filter(x => x.type() === filterName)[0];
        if (existing) {
            existing.removeFilter();
        }

        return this;
    }

    /**
     * @public
     * @expose
     * @returns {FiltersController}
     */
    destroy() {
        if (this._addFilterButton) {
            this._addFilterButton.remove();
        }
        if (this._currentFilters) {
            for (let i = 0; i < this._currentFilters.length; i++) {
                this._currentFilters[i].remove();
            }
            this._currentFilters.length = 0;
        }

        return this;
    }
}

export default FiltersController;

